import { useState, useCallback, useEffect, useContext } from 'react';
import { VatContext } from '../../../contexts/VatContext';
import { CustomerExperienceCookie } from '../../../types';

const COOKIE_NAME = 'CEXP';

export const useCustomerExperienceContextModule = () => {
  const [cookieState, setCookieState] = useState<
    CustomerExperienceCookie | undefined
  >(undefined);
  const { vatLocale, vatPop } = useContext(VatContext);

  const defaultCustomerExperiences = {
    vatPopEnabled: !!(vatLocale && vatPop),
    vatPopShown: false,
    persistVatPopOnNewPage: false,
  };

  const getCookieValue = () => {
    try {
      const cookie = JSON.parse(
        atob(
          //This is apparently the standard way of pulling the value of the cookie in vanilla javascript, where "CEXP" is the name of the cookie
          // eslint-disable-next-line prettier/prettier
          document.cookie.replace(
            /(?:(?:^|.*;\s*)CEXP\s*\=\s*([^;]*).*$)|^.*$/,
            '$1'
          )
        )
      );
      return { ...defaultCustomerExperiences, ...cookie }; //return the default overridden with any differences from the browser cookies
    } catch (ex) {
      return defaultCustomerExperiences;
    }
  };

  const setCookieValue = (experiences: CustomerExperienceCookie) => {
    const cexpCookie = `${COOKIE_NAME}=${btoa(
      JSON.stringify(experiences)
    )};domain=${getDomain()};path=/`;
    document.cookie = cexpCookie;
    setCookieState(experiences);
  };

  // Initialize the cookie only once.
  useEffect(() => {
    const cookie = getCookieValue();
    //synchronize react cookie state with browser cookie state
    setCookieValue(cookie);
  }, []);

  const setVatPopShown = useCallback(
    vatPopShown => {
      if (cookieState && cookieState.vatPopShown != vatPopShown) {
        cookieState.vatPopShown = vatPopShown;
        setCookieValue(cookieState);
      }
    },
    [cookieState]
  );
  const setPersistVatPop = useCallback(
    shouldPersist => {
      if (cookieState && cookieState.persistVatPopOnNewPage != shouldPersist) {
        cookieState.persistVatPopOnNewPage = shouldPersist;
        setCookieValue(cookieState);
      }
    },
    [cookieState]
  );

  return {
    setVatPopShown,
    setPersistVatPop,
    vatPopShown: cookieState
      ? cookieState.vatPopShown && !cookieState.persistVatPopOnNewPage
      : true,
  };
};

const allowedDomains = ['.vpsvc', '.vistaprint', '.promotique'];

const getDomain = () => {
  const currentDomain = document.location?.host;

  if (currentDomain.indexOf('localhost') >= 0) {
    // Can't use the domain attribute for cookies on localhost https://stackoverflow.com/questions/1134290/cookies-on-localhost-with-explicit-domain
    return '';
  }

  for (var domain of allowedDomains) {
    let domainIndex = currentDomain.indexOf(domain);
    if (domainIndex >= 0) {
      return currentDomain.slice(domainIndex);
    }
  }
  return currentDomain;
};
